import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Sparkles, Pencil, Trash2, ExternalLink, Globe, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const LOCALES = ["it", "ro", "en"];

export default function SEOPages() {
  const [pages, setPages] = useState([]);
  const [q, setQ] = useState("");
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ direction: "it_to_eu", origin_level: "city", dest_level: "country", dest_country: "RO", origin_country: "ALL", publish: true });
  const [countries, setCountries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editLocale, setEditLocale] = useState("it");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/seo/pages").then(({ data }) => setPages(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/seo/geo-data").then(({ data }) => setCountries(data?.destinations?.countries || [])).catch(() => {});
  }, []);

  // Detailed county/city catalog only exists for these destination countries.
  const hasDetail = gen.dest_country === "RO";

  const runGenerate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/seo/pages/bulk-generate", gen);
      toast.success(`${data.created} pagine generate · ${data.skipped} già esistenti`);
      setGenOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const [mapLoading, setMapLoading] = useState(false);
  const runMap = async () => {
    if (!window.confirm("Genera la mappa SEO completa (Italia ↔ tutta Europa)? Verranno create centinaia di pagine pubblicate. Le duplicate vengono ignorate.")) return;
    setMapLoading(true);
    try {
      const { data } = await api.post("/seo/pages/generate-map", { publish: true });
      toast.success(`Mappa generata: ${data.created} nuove pagine · ${data.skipped} già esistenti`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMapLoading(false); }
  };

  const toggleStatus = async (p) => {
    const status = p.status === "published" ? "draft" : "published";
    await api.patch(`/seo/pages/${p.id}`, { status });
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare ${p.slug}?`)) return;
    await api.delete(`/seo/pages/${p.id}`); load();
  };

  const openEdit = async (p) => {
    const { data } = await api.get(`/seo/pages/${p.id}`);
    setEditLocale("it");
    setEditing(data);
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/seo/pages/${editing.id}`, {
        slug: editing.slug, status: editing.status, overrides: editing.overrides || {},
      });
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setOverride = (field, value) => {
    setEditing((prev) => ({
      ...prev,
      overrides: { ...(prev.overrides || {}), [editLocale]: { ...((prev.overrides || {})[editLocale] || {}), [field]: value } },
    }));
  };
  const ov = (field) => (editing?.overrides?.[editLocale]?.[field]) ?? "";
  const ph = (field) => editing?.preview?.[editLocale]?.[field] || "";

  const filtered = pages.filter((p) => !q || p.slug.includes(q.toLowerCase()) ||
    (p.origin_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (p.dest_name || "").toLowerCase().includes(q.toLowerCase()));

  const totals = pages.reduce((a, p) => ({
    visits: a.visits + (p.analytics?.visits || 0),
    quotes: a.quotes + (p.analytics?.quote_requests || 0),
    orders: a.orders + (p.analytics?.orders || 0),
    revenue: a.revenue + (p.analytics?.revenue || 0),
  }), { visits: 0, quotes: 0, orders: 0, revenue: 0 });

  return (
    <div className="space-y-6" data-testid="seo-pages-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Globe size={26} /> SEO Landing Pages</h1>
          <p className="text-sm text-slate-500 mt-1">Italia → Europa · genera traffico organico e converti in preventivi.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runMap} disabled={mapLoading} data-testid="seo-generate-map-btn"><Globe size={16} className="mr-1.5" /> {mapLoading ? "Generazione…" : "Genera mappa completa"}</Button>
          <Button onClick={() => setGenOpen(true)} className="bg-blue-900 hover:bg-blue-800" data-testid="seo-generate-btn"><Sparkles size={16} className="mr-1.5" /> Genera pagine</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[["Pagine", pages.length], ["Visite", totals.visits], ["Preventivi", totals.quotes], ["Ordini", totals.orders], ["Ricavi", `€${totals.revenue.toFixed(0)}`]].map(([l, v]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-wider text-slate-400">{l}</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca slug / città…" className="pl-9" data-testid="seo-search" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="seo-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            {["Tratta", "Slug", "Stato", "Visite", "Prev.", "Ord.", "Conv.", "Ricavi", ""].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-500">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Nessuna pagina. Clicca "Genera pagine".</TableCell></TableRow>
              : filtered.map((p) => (
                <TableRow key={p.id} data-testid="seo-row">
                  <TableCell className="text-sm font-medium">{p.origin_name} → {p.dest_name}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{p.slug}</TableCell>
                  <TableCell>
                    <button onClick={() => toggleStatus(p)} data-testid={`seo-status-${p.id}`}
                      className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {p.status === "published" ? <Eye size={11} /> : <EyeOff size={11} />}{p.status === "published" ? "Live" : "Draft"}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm">{p.analytics?.visits || 0}</TableCell>
                  <TableCell className="text-sm">{p.analytics?.quote_requests || 0}</TableCell>
                  <TableCell className="text-sm">{p.analytics?.orders || 0}</TableCell>
                  <TableCell className="text-sm">{p.analytics?.conversion_rate || 0}%</TableCell>
                  <TableCell className="text-sm">€{(p.analytics?.revenue || 0).toFixed(0)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <a href={`/${p.slug}`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" data-testid={`seo-view-${p.id}`}><ExternalLink size={15} /></Button></a>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`seo-edit-${p.id}`}><Pencil size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`seo-delete-${p.id}`}><Trash2 size={15} className="text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Bulk generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="seo-generate-dialog">
          <DialogHeader><DialogTitle>Genera pagine SEO</DialogTitle>
            <DialogDescription>Crea automaticamente pagine SEO Italia → Europa per più tratte.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Direzione</Label>
              <Select value={gen.direction} onValueChange={(v) => setGen({ ...gen, direction: v, dest_level: "country" })}>
                <SelectTrigger data-testid="gen-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="it_to_eu">Italia → Europa</SelectItem>
                  <SelectItem value="eu_to_it">Europa → Italia</SelectItem>
                </SelectContent>
              </Select></div>

            {gen.direction === "it_to_eu" ? (
              <>
                <div className="space-y-1.5"><Label>Origine (Italia)</Label>
                  <Select value={gen.origin_level} onValueChange={(v) => setGen({ ...gen, origin_level: v })}>
                    <SelectTrigger data-testid="gen-origin-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="region">Regioni</SelectItem>
                      <SelectItem value="city">Città / Province</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Paese di destinazione</Label>
                  <Select value={gen.dest_country} onValueChange={(v) => setGen({ ...gen, dest_country: v, dest_level: v === "RO" ? gen.dest_level : "country" })}>
                    <SelectTrigger data-testid="gen-dest-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name_it}{c.code === "RO" ? " (contee/città disponibili)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Livello destinazione</Label>
                  <Select value={gen.dest_level} onValueChange={(v) => setGen({ ...gen, dest_level: v })} disabled={!hasDetail}>
                    <SelectTrigger data-testid="gen-dest-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="country">Paese (1 pagina per origine)</SelectItem>
                      {hasDetail && <SelectItem value="county">Contee / Județe</SelectItem>}
                      {hasDetail && <SelectItem value="city">Città</SelectItem>}
                    </SelectContent>
                  </Select>
                  {!hasDetail && <p className="text-xs text-slate-400">Per questo paese sono disponibili pagine a livello nazionale.</p>}
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5"><Label>Paese di origine (Europa)</Label>
                  <Select value={gen.origin_country} onValueChange={(v) => setGen({ ...gen, origin_country: v })}>
                    <SelectTrigger data-testid="gen-origin-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Tutta Europa (tutti i paesi)</SelectItem>
                      {countries.filter((c) => c.code !== "RO" || true).map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name_it}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Destinazione (Italia)</Label>
                  <Select value={gen.dest_level} onValueChange={(v) => setGen({ ...gen, dest_level: v })}>
                    <SelectTrigger data-testid="gen-dest-level-it"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="country">Italia (paese)</SelectItem>
                      <SelectItem value="region">Regioni italiane</SelectItem>
                      <SelectItem value="city">Città italiane</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Es. "Tutta Europa" × "Città italiane" genera molte pagine (es. Germania → Milano).</p>
                </div>
              </>
            )}

            <div className="flex items-center justify-between">
              <Label>Pubblica subito</Label>
              <Switch checked={gen.publish} onCheckedChange={(v) => setGen({ ...gen, publish: v })} data-testid="gen-publish" />
            </div>
            <p className="text-xs text-slate-400">Le pagine duplicate vengono ignorate automaticamente. Ogni pagina ha testo unico (IT/RO/EN).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Annulla</Button>
            <Button onClick={runGenerate} disabled={loading} className="bg-blue-900 hover:bg-blue-800" data-testid="gen-confirm">{loading ? "…" : "Genera"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="seo-edit-dialog">
          <DialogHeader><DialogTitle>{editing?.origin_name} → {editing?.dest_name}</DialogTitle>
            <DialogDescription>Modifica slug, stato e i testi SEO per lingua (IT/RO/EN).</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Slug</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="seo-edit-slug" /></div>
                <div className="space-y-1.5"><Label>Stato</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger data-testid="seo-edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Pubblicato</SelectItem></SelectContent>
                  </Select></div>
              </div>
              <div className="flex gap-2">
                {LOCALES.map((l) => (
                  <button key={l} onClick={() => setEditLocale(l)} data-testid={`seo-edit-lang-${l}`}
                    className={`text-xs px-3 py-1.5 rounded-full uppercase ${editLocale === l ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-500"}`}>{l}</button>
                ))}
              </div>
              {[["meta_title", "Meta title"], ["meta_description", "Meta description"], ["h1", "H1"]].map(([f, lbl]) => (
                <div key={f} className="space-y-1.5"><Label>{lbl}</Label>
                  <Input value={ov(f)} placeholder={ph(f)} onChange={(e) => setOverride(f, e.target.value)} data-testid={`seo-edit-${f}`} /></div>
              ))}
              <div className="space-y-1.5"><Label>Contenuto</Label>
                <Textarea rows={4} value={ov("content")} placeholder={ph("content")} onChange={(e) => setOverride("content", e.target.value)} data-testid="seo-edit-content" /></div>
              <p className="text-xs text-slate-400">Lascia vuoto per usare il testo generato automaticamente (mostrato come placeholder).</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={saveEdit} className="bg-blue-900 hover:bg-blue-800" data-testid="seo-edit-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
